/**
 * Where to go after signing in, carried as `?next=` on /signin.
 *
 * The coin shop on nsibi.app hands a card purchase here as
 * `/wallet?pay=card&pack=<id>` (or `&amount=<usd cents>`); signed out, that
 * becomes `/signin?next=<it>`, and signing in lands back on it, where the
 * wallet opens the card checkout by itself.
 *
 * `next` comes from a URL anybody can write, so only a path on THIS site is
 * followed: one leading slash, never `//host` or `/\host`, which a browser
 * reads as another origin.
 */
export function safeNext(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  return raw;
}

/** The signin URL that comes back to `pathAndSearch` afterwards. */
export function signInReturningTo(pathAndSearch: string): string {
  return `/signin?next=${encodeURIComponent(pathAndSearch)}`;
}

/**
 * A card purchase handed over by the coin shop: what to buy, and the
 * account it is FOR (`to`, the shop's pointer; absent means the buyer's own
 * account), or null. The server decides what `to` names - a forged one
 * finds nobody - so it is passed through untouched.
 */
export type CardPurchase = ({ packId: string } | { usdCents: number }) & { to?: string };

export function cardPurchaseFrom(search: string): CardPurchase | null {
  const params = new URLSearchParams(search);
  if (params.get('pay') !== 'card') return null;
  const to = (params.get('to') ?? '').slice(0, 400) || undefined;
  const packId = params.get('pack');
  if (packId) return to ? { packId, to } : { packId };
  const cents = Number(params.get('amount'));
  if (!Number.isInteger(cents) || cents <= 0) return null;
  return to ? { usdCents: cents, to } : { usdCents: cents };
}

/** Whether a `next` path is a card purchase, for the sign-in page's words. */
export function isCardPurchase(next: string | null): boolean {
  if (!next) return false;
  const q = next.indexOf('?');
  return next.startsWith('/wallet') && q >= 0 && cardPurchaseFrom(next.slice(q)) !== null;
}
